import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// Constants
const MAX_HISTORY_SIZE = 50; // Reduced from 100 to save memory
const DEBOUNCE_DELAY = 100; // ms - reduced for better responsiveness
const ERASE_DEBOUNCE_DELAY = 50; // ms - even shorter for erase operations
const ERASE_PAUSE_THRESHOLD = 200; // ms - if pause between erases is longer, flush immediately
const BATCH_SIZE = 5; // Reduced batch size for more granular undo/redo
const STORAGE_KEY = 'dnd_map_history';
const STORAGE_VERSION = '1.2'; // Bumped version to clear old data after compression improvements
const MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB limit for localStorage (reduced to prevent quota issues)

// Command types
export const COMMAND_TYPES = {
  // Drawing commands
  DRAW_LINE: 'DRAW_LINE',
  DRAW_SHAPE: 'DRAW_SHAPE',
  ERASE: 'ERASE',
  
  // Token commands
  ADD_TOKEN: 'ADD_TOKEN',
  MOVE_TOKEN: 'MOVE_TOKEN',
  UPDATE_TOKEN: 'UPDATE_TOKEN',
  DELETE_TOKEN: 'DELETE_TOKEN',
  
  // Token grouping commands
  CREATE_TOKEN_GROUP: 'CREATE_TOKEN_GROUP',
  REMOVE_TOKEN_GROUP: 'REMOVE_TOKEN_GROUP',
  
  // Layer commands
  ADD_LAYER: 'ADD_LAYER',
  DELETE_LAYER: 'DELETE_LAYER',
  UPDATE_LAYER: 'UPDATE_LAYER',
  REORDER_LAYERS: 'REORDER_LAYERS',
  
  // Layer grouping commands
  CREATE_LAYER_GROUP: 'CREATE_LAYER_GROUP',
  DELETE_LAYER_GROUP: 'DELETE_LAYER_GROUP',
  UPDATE_LAYER_GROUP_COLOR: 'UPDATE_LAYER_GROUP_COLOR',
  ADD_LAYER_TO_GROUP: 'ADD_LAYER_TO_GROUP',
  REMOVE_LAYER_FROM_GROUP: 'REMOVE_LAYER_FROM_GROUP',
  
  // Container commands
  ADD_CONTAINER: 'ADD_CONTAINER',
  UPDATE_CONTAINER: 'UPDATE_CONTAINER',
  DELETE_CONTAINER: 'DELETE_CONTAINER',
  MOVE_CONTAINER_WITH_DRAWINGS: 'MOVE_CONTAINER_WITH_DRAWINGS',
  
  // Container management commands (new)
  TOGGLE_LOCK_CONTAINER: 'TOGGLE_LOCK_CONTAINER',
  CHANGE_CONTAINER_Z_INDEX: 'CHANGE_CONTAINER_Z_INDEX',
  DUPLICATE_CONTAINER: 'DUPLICATE_CONTAINER',
  
  // Container drawing commands
  ADD_DRAWING_TO_CONTAINER: 'ADD_DRAWING_TO_CONTAINER',
  UPDATE_DRAWING_IN_CONTAINER: 'UPDATE_DRAWING_IN_CONTAINER',
  DELETE_DRAWING_FROM_CONTAINER: 'DELETE_DRAWING_FROM_CONTAINER',
  
  // Layer image operations
  ADD_IMAGE_TO_EXISTING_LAYER: 'ADD_IMAGE_TO_EXISTING_LAYER',
  ADD_IMAGE_WITH_NEW_LAYERS: 'ADD_IMAGE_WITH_NEW_LAYERS',
  
  // Batch command for multiple operations
  BATCH: 'BATCH'
};

// Utility functions for differential storage
const createDelta = (previousState, newState) => {
  if (!previousState || !newState) return { full: newState };
  
  const delta = { changes: {} };
  
  // For objects, compare properties
  if (typeof newState === 'object' && typeof previousState === 'object') {
    for (const key in newState) {
      if (newState[key] !== previousState[key]) {
        delta.changes[key] = {
          old: previousState[key],
          new: newState[key]
        };
      }
    }
    
    // Check for removed properties
    for (const key in previousState) {
      if (!(key in newState)) {
        delta.changes[key] = {
          old: previousState[key],
          new: undefined
        };
      }
    }
    
    // If no changes, return null
    if (Object.keys(delta.changes).length === 0) return null;
  } else {
    // For primitives or different types, store full values
    delta.full = { old: previousState, new: newState };
  }
  
  return delta;
};

const applyDelta = (baseState, delta, reverse = false) => {
  if (!delta) return baseState;
  
  if (delta.full) {
    return reverse ? delta.full.old : delta.full.new;
  }
  
  if (delta.changes) {
    const result = { ...baseState };
    for (const key in delta.changes) {
      const change = delta.changes[key];
      result[key] = reverse ? change.old : change.new;
      
      // Remove undefined properties
      if (result[key] === undefined) {
        delete result[key];
      }
    }
    return result;
  }
  
  return baseState;
};

// Utility to estimate storage size
const estimateStorageSize = (data) => {
  return new Blob([JSON.stringify(data)]).size;
};

// Utility to compress history data
const compressHistoryData = (history) => {
  return history.map(command => {
    // Remove large data that can be reconstructed
    const compressed = { ...command };
    
    // For drawing commands, keep only essential data
    if (command.type === 'DRAW_LINE' || command.type === 'DRAW_SHAPE') {
      if (compressed.newState && compressed.newState.lines) {
        // Keep only last 10 lines to reduce size
        compressed.newState = {
          ...compressed.newState,
          lines: compressed.newState.lines.slice(-10)
        };
      }
      if (compressed.previousState && compressed.previousState.lines) {
        compressed.previousState = {
          ...compressed.previousState,
          lines: compressed.previousState.lines.slice(-10)
        };
      }
    }
    
    // For container commands, intelligently handle large image data
    // We now aggressively compress ALL images, not just large ones, for history storage
    if (command.type && (command.type.includes('CONTAINER') || command.type.includes('IMAGE'))) {
      // Remove large image data but preserve essential metadata for reconstruction
      if (compressed.newState && compressed.newState.image && compressed.newState.image.src) {
        const imageSrc = compressed.newState.image.src;
        // Compress ALL images for history storage to prevent localStorage overflow
        if (imageSrc && imageSrc.startsWith('data:') && imageSrc.length > 100000) { // >100KB
          compressed.newState = {
            ...compressed.newState,
            image: {
              ...compressed.newState.image,
              src: `[COMPRESSED_IMAGE_${imageSrc.length}_BYTES]`,
              // Keep ALL essential metadata for reconstruction
              width: compressed.newState.image.width,
              height: compressed.newState.image.height,
              originalAspectRatio: compressed.newState.image.originalAspectRatio,
              // Store a tiny thumbnail if needed for UI preview (optional)
              compressed: true
            }
          };
        }
      }
      if (compressed.previousState && compressed.previousState.image && compressed.previousState.image.src) {
        const imageSrc = compressed.previousState.image.src;
        // Compress ALL images for history storage to prevent localStorage overflow
        if (imageSrc && imageSrc.startsWith('data:') && imageSrc.length > 100000) { // >100KB
          compressed.previousState = {
            ...compressed.previousState,
            image: {
              ...compressed.previousState.image,
              src: `[COMPRESSED_IMAGE_${imageSrc.length}_BYTES]`,
              // Keep ALL essential metadata for reconstruction
              width: compressed.previousState.image.width,
              height: compressed.previousState.image.height,
              originalAspectRatio: compressed.previousState.image.originalAspectRatio,
              // Store a tiny thumbnail if needed for UI preview (optional)
              compressed: true
            }
          };
        }
      }
      
      // Also handle containerData field which might contain images
      if (compressed.containerData && compressed.containerData.image && compressed.containerData.image.src) {
        const imageSrc = compressed.containerData.image.src;
        if (imageSrc && imageSrc.startsWith('data:') && imageSrc.length > 100000) {
          compressed.containerData = {
            ...compressed.containerData,
            image: {
              ...compressed.containerData.image,
              src: `[COMPRESSED_IMAGE_${imageSrc.length}_BYTES]`,
              width: compressed.containerData.image.width,
              height: compressed.containerData.image.height,
              originalAspectRatio: compressed.containerData.image.originalAspectRatio,
              compressed: true
            }
          };
        }
      }
    }
    
    // Remove very large properties
    if (compressed.newState && JSON.stringify(compressed.newState).length > 50000) {
      compressed.newState = { compressed: true, type: command.type };
    }
    if (compressed.previousState && JSON.stringify(compressed.previousState).length > 50000) {
      compressed.previousState = { compressed: true, type: command.type };
    }
    
    return compressed;
  });
};

// localStorage utilities
const saveToStorage = (history, currentIndex) => {
  try {
    // Compress and limit history data more aggressively
    let compressedHistory = compressHistoryData(history.slice(-50)); // Keep only last 50 commands
    
    let data = {
      version: STORAGE_VERSION,
      history: compressedHistory,
      currentIndex: Math.min(currentIndex, compressedHistory.length - 1),
      timestamp: Date.now()
    };
    
    // Check size before saving and reduce further if needed
    let estimatedSize = estimateStorageSize(data);
    
    // If still too large, reduce history even more
    if (estimatedSize > MAX_STORAGE_SIZE) {
      console.warn(`[HistoryContext] Data still too large (${(estimatedSize / 1024 / 1024).toFixed(2)}MB), reducing history to 25 commands`);
      compressedHistory = compressHistoryData(history.slice(-25)); // Keep only last 25 commands
      
      data = {
        version: STORAGE_VERSION,
        history: compressedHistory,
        currentIndex: Math.min(currentIndex, compressedHistory.length - 1),
        timestamp: Date.now()
      };
      
      estimatedSize = estimateStorageSize(data);
    }
    
    // If STILL too large, reduce to 10 commands
    if (estimatedSize > MAX_STORAGE_SIZE) {
      console.warn(`[HistoryContext] Data still too large (${(estimatedSize / 1024 / 1024).toFixed(2)}MB), reducing history to 10 commands`);
      compressedHistory = compressHistoryData(history.slice(-10)); // Keep only last 10 commands
      
      data = {
        version: STORAGE_VERSION,
        history: compressedHistory,
        currentIndex: Math.min(currentIndex, compressedHistory.length - 1),
        timestamp: Date.now()
      };
      
      estimatedSize = estimateStorageSize(data);
    }
    
    // Final check - if STILL too large, skip saving
    if (estimatedSize > MAX_STORAGE_SIZE) {
      console.warn(`[HistoryContext] Data too large for localStorage (${(estimatedSize / 1024 / 1024).toFixed(2)}MB), skipping save`);
      return;
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('[HistoryContext] Failed to save to localStorage:', error);
    // If quota exceeded, try to clear old data and save minimal version
    if (error.name === 'QuotaExceededError') {
      try {
        localStorage.removeItem(STORAGE_KEY);
        const minimalData = {
          version: STORAGE_VERSION,
          history: [], // Start fresh
          currentIndex: -1,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(minimalData));
      } catch (retryError) {
        console.warn('[HistoryContext] Failed to save even minimal data:', retryError);
      }
    }
  }
};

const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    
    // Check version compatibility
    if (data.version !== STORAGE_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    // Check if data is not too old (24 hours)
    const maxAge = 24 * 60 * 60 * 1000;
    if (Date.now() - data.timestamp > maxAge) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    return {
      history: data.history || [],
      currentIndex: data.currentIndex || -1
    };
  } catch (error) {
    console.warn('[HistoryContext] Failed to load from localStorage:', error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

// Create the context
const HistoryContext = createContext(null);

// Custom hook to use the history context
export const useHistory = () => {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('useHistory must be used within a HistoryProvider');
  }
  return context;
};

// History Provider component
export const HistoryProvider = ({ children }) => {
  const [history, setHistory] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Refs for debouncing and batching
  const debounceTimeoutRef = useRef(null);
  const pendingCommandsRef = useRef([]);
  const lastStateRef = useRef({});
  const lastEraseTimeRef = useRef(0);
  
  // Cleanup function to remove old localStorage data
  const cleanupOldStorage = useCallback(() => {
    try {
      // Remove any old storage keys that might exist
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dnd_map_') && key !== STORAGE_KEY) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Also check current storage size and clean if needed
      const currentData = localStorage.getItem(STORAGE_KEY);
      if (currentData && estimateStorageSize(JSON.parse(currentData)) > MAX_STORAGE_SIZE) {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.warn('[HistoryContext] Error during cleanup:', error);
    }
  }, []);

  // Load history from localStorage on mount
  useEffect(() => {
    // Clean up old data first
    cleanupOldStorage();
    
    const stored = loadFromStorage();
    if (stored) {
      setHistory(stored.history);
      setCurrentIndex(stored.currentIndex);
    }
    setIsLoaded(true);
  }, [cleanupOldStorage]);
  
  // Save to localStorage when history changes (heavily debounced)
  useEffect(() => {
    if (!isLoaded) return; // Don't save during initial load
    
    const saveDebounced = () => {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        // Only save if there are actual changes and not too frequently
        const now = Date.now();
        const lastSave = debounceTimeoutRef.lastSave || 0;
        
        // Don't save more than once every 5 seconds
        if (now - lastSave < 5000) {
          return;
        }
        
        saveToStorage(history, currentIndex);
        debounceTimeoutRef.lastSave = now;
      }, 3000); // Save 3 seconds after last change (increased from 1 second)
    };
    
    saveDebounced();
    
    return () => clearTimeout(debounceTimeoutRef.current);
  }, [history, currentIndex, isLoaded]);
  
  // Flush pending commands to history
  const flushPendingCommands = useCallback(() => {
    if (pendingCommandsRef.current.length === 0) return;
    
    const commands = [...pendingCommandsRef.current];
    pendingCommandsRef.current = [];
    
    setHistory(prevHistory => {
      // Remove any future history if we're not at the end
      const newHistory = prevHistory.slice(0, currentIndex + 1);
      
      // If we're removing future history, we need to clean up lastStateRef
      // to prevent stale state references
      if (newHistory.length < prevHistory.length) {
        // Keep only the state references that are still relevant
        const relevantTargetIds = new Set();
        newHistory.forEach(cmd => {
          if (cmd.targetId) relevantTargetIds.add(cmd.targetId);
          if (cmd.commands) {
            cmd.commands.forEach(subCmd => {
              if (subCmd.targetId) relevantTargetIds.add(subCmd.targetId);
            });
          }
        });
        
        // Clean up lastStateRef to only keep relevant targets
        Object.keys(lastStateRef.current).forEach(targetId => {
          if (!relevantTargetIds.has(targetId)) {
            delete lastStateRef.current[targetId];
          }
        });
      }
      
      // Normalize commands: flatten any nested BATCH commands and remove falsy entries
      const normalizeCommands = (cmds) => {
        const flat = [];
        cmds.forEach(c => {
          if (!c) return;
          if (c.type === COMMAND_TYPES.BATCH && Array.isArray(c.commands)) {
            // Flatten nested batch
            c.commands.forEach(sc => { if (sc) flat.push(sc); });
          } else {
            flat.push(c);
          }
        });
        return flat;
      };

      const normalized = normalizeCommands(commands);
      if (normalized.length === 0) {
        return newHistory; // Nothing to push
      }

      // Push commands individually to avoid creating standalone BATCH entries here.
      // Explicit batching (e.g., container + layer) should be created by the producer.
      normalized.forEach(command => {
        const newCommand = {
          ...command,
          id: `cmd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: Date.now()
        };
        newHistory.push(newCommand);
      });
      
      // Limit history size and clean up memory
      if (newHistory.length > MAX_HISTORY_SIZE) {
        const removeCount = newHistory.length - MAX_HISTORY_SIZE;
        
        // Clean up lastStateRef for removed commands
        const removedCommands = newHistory.slice(0, removeCount);
        removedCommands.forEach(cmd => {
          if (cmd.targetId && lastStateRef.current[cmd.targetId]) {
            delete lastStateRef.current[cmd.targetId];
          }
          if (cmd.commands) {
            cmd.commands.forEach(subCmd => {
              if (subCmd.targetId && lastStateRef.current[subCmd.targetId]) {
                delete lastStateRef.current[subCmd.targetId];
              }
            });
          }
        });
        
        newHistory.splice(0, removeCount);
        setCurrentIndex(prev => Math.max(prev - removeCount, 0));
        return newHistory;
      }
      
      // Update current index to point to the new command(s)
      setCurrentIndex(newHistory.length - 1);
      
      return newHistory;
    });
  }, [currentIndex]);
  
  // Enhanced push command with debouncing and differential storage
  const pushCommand = useCallback((command) => {
    // Preserve explicit BATCH commands as single atomic history entries
    if (command && command.type === COMMAND_TYPES.BATCH && Array.isArray(command.commands)) {
      // Flush any pending commands first to keep order
      if (pendingCommandsRef.current.length > 0) {
        flushPendingCommands();
      }
      // Push the batch directly to history
      setHistory(prevHistory => {
        const newHistory = prevHistory.slice(0, currentIndex + 1);
        const batchCommand = {
          ...command,
          id: `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: Date.now(),
          // Ensure sub-commands have ids/timestamps
          commands: command.commands.map(cmd => ({
            ...cmd,
            id: cmd.id || `cmd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: cmd.timestamp || Date.now()
          }))
        };
        newHistory.push(batchCommand);
        setCurrentIndex(newHistory.length - 1);
        return newHistory;
      });
      return;
    }

    // Create differential storage for drawing commands (but not ERASE - it has its own logic)
    if (['DRAW_LINE', 'DRAW_SHAPE'].includes(command.type)) {
      // Use command.previousState as baseline when we have no lastState recorded yet
      const baselineState =
        lastStateRef.current[command.targetId] !== undefined
          ? lastStateRef.current[command.targetId]
          : command.previousState;

      const delta = createDelta(baselineState, command.newState);

      if (!delta) {
        return;
      }

      command.delta = delta;
      lastStateRef.current[command.targetId] = command.newState;
    }
    
    // For ERASE commands, always update lastStateRef but don't create delta here
    // (ERASE commands handle their own change detection in MapDisplay.js)
    if (command.type === 'ERASE') {
      lastStateRef.current[command.targetId] = command.newState;
    }
    
    // Special handling for ERASE commands
    if (command.type === 'ERASE') {
      const currentTime = Date.now();
      const pendingEraseCommands = pendingCommandsRef.current.filter(cmd => cmd.type === 'ERASE');
      
      // Check if there's been a pause in erasing (longer than threshold)
      const timeSinceLastErase = currentTime - lastEraseTimeRef.current;
      const hasBeenPause = timeSinceLastErase > ERASE_PAUSE_THRESHOLD;
      
      // If we already have several erase commands pending, flush them first
      if (pendingEraseCommands.length >= 3) {
        flushPendingCommands();
      }
      // If there's been a pause and we have pending erases, flush them first
      else if (hasBeenPause && pendingEraseCommands.length > 0) {
        flushPendingCommands();
      }
      
      // For single erase operations (no pending erase commands), flush immediately
      // This handles quick single clicks that should be recorded immediately
      if (pendingCommandsRef.current.filter(cmd => cmd.type === 'ERASE').length === 0) {
        pendingCommandsRef.current.push(command);
        flushPendingCommands();
        lastEraseTimeRef.current = currentTime;
        return; // Exit early, command is already processed
      }
      
      // Update last erase time
      lastEraseTimeRef.current = currentTime;
    }
    
    // Add to pending commands
    pendingCommandsRef.current.push(command);
    
    // Clear existing debounce timeout
    clearTimeout(debounceTimeoutRef.current);
    
    // Determine if we should batch or flush immediately
    const shouldFlushImmediately = 
      !['DRAW_LINE', 'ERASE'].includes(command.type) || // Non-drawing commands
      pendingCommandsRef.current.length >= BATCH_SIZE; // Batch size reached
    
    if (shouldFlushImmediately) {
      flushPendingCommands();
    } else {
      // Use shorter debounce for erase operations for better granularity
      const delayToUse = command.type === 'ERASE' ? ERASE_DEBOUNCE_DELAY : DEBOUNCE_DELAY;
      
      // Debounce for drawing commands
      debounceTimeoutRef.current = setTimeout(() => {
        flushPendingCommands();
      }, delayToUse);
    }
  }, [flushPendingCommands, currentIndex]);
  
  // Enhanced undo with differential storage support
  const undo = useCallback(() => {
    if (currentIndex < 0) {
      return null;
    }
    
    // Flush any pending commands first
    if (pendingCommandsRef.current.length > 0) {
      flushPendingCommands();
      // Wait for state update before proceeding
      setTimeout(() => undo(), 0);
      return null;
    }
    
    const command = history[currentIndex];
    
    // Update lastStateRef for drawing commands to maintain consistency
    if (command.type && ['DRAW_LINE', 'DRAW_SHAPE', 'ERASE'].includes(command.type)) {
      if (command.targetId && command.previousState) {
        lastStateRef.current[command.targetId] = command.previousState;
      }
    } else if (command.type === COMMAND_TYPES.BATCH && command.commands) {
      // Handle batch commands
      command.commands.forEach(cmd => {
        if (cmd.type && ['DRAW_LINE', 'DRAW_SHAPE', 'ERASE'].includes(cmd.type)) {
          if (cmd.targetId && cmd.previousState) {
            lastStateRef.current[cmd.targetId] = cmd.previousState;
          }
        }
      });
    }
    
    setCurrentIndex(prev => prev - 1);
    
    return command;
  }, [history, currentIndex, flushPendingCommands]);
  
  // Enhanced redo with differential storage support
  const redo = useCallback(() => {
    if (currentIndex >= history.length - 1) {
      return null;
    }
    
    // Flush any pending commands first
    if (pendingCommandsRef.current.length > 0) {
      flushPendingCommands();
      // Wait for state update before proceeding
      setTimeout(() => redo(), 0);
      return null;
    }
    
    const nextIndex = currentIndex + 1;
    const command = history[nextIndex];
    
    // Update lastStateRef for drawing commands to maintain consistency
    if (command.type && ['DRAW_LINE', 'DRAW_SHAPE', 'ERASE'].includes(command.type)) {
      if (command.targetId && command.newState) {
        lastStateRef.current[command.targetId] = command.newState;
      }
    } else if (command.type === COMMAND_TYPES.BATCH && command.commands) {
      // Handle batch commands
      command.commands.forEach(cmd => {
        if (cmd.type && ['DRAW_LINE', 'DRAW_SHAPE', 'ERASE'].includes(cmd.type)) {
          if (cmd.targetId && cmd.newState) {
            lastStateRef.current[cmd.targetId] = cmd.newState;
          }
        }
      });
    }
    
    setCurrentIndex(nextIndex);
    
    return command;
  }, [history, currentIndex, flushPendingCommands]);
  
  // Check if we can undo
  const canUndo = useCallback(() => {
    return currentIndex >= 0 || pendingCommandsRef.current.length > 0;
  }, [currentIndex]);
  
  // Check if we can redo
  const canRedo = useCallback(() => {
    return currentIndex < history.length - 1;
  }, [currentIndex, history.length]);
  
  // Clear all history
  const clearHistory = useCallback(() => {
    clearTimeout(debounceTimeoutRef.current);
    pendingCommandsRef.current = [];
    lastStateRef.current = {};
    lastEraseTimeRef.current = 0;
    setHistory([]);
    setCurrentIndex(-1);
    localStorage.removeItem(STORAGE_KEY);
  }, []);
  
  // Get current localStorage usage
  const getCurrentStorageSize = useCallback(() => {
    try {
      let total = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          total += localStorage[key].length + key.length;
        }
      }
      return total;
    } catch (error) {
      return 0;
    }
  }, []);

  // Manual cleanup function for UI
  const performCleanup = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY);
    
    // Keep only last 20 commands (increased to preserve more undo capability)
    const keepCount = Math.min(20, history.length);
    const newHistory = history.slice(-keepCount);
    
    // Clean up memory references
    lastStateRef.current = {};
    pendingCommandsRef.current = [];
    
    setHistory(newHistory);
    setCurrentIndex(Math.min(currentIndex, newHistory.length - 1));
    
    // Force save the cleaned data
    setTimeout(() => {
      saveToStorage(newHistory, Math.min(currentIndex, newHistory.length - 1));
    }, 100);
    
    return {
      before: history.length,
      after: newHistory.length,
      memoryFreed: JSON.stringify(history).length - JSON.stringify(newHistory).length
    };
  }, [history, currentIndex]);

  // Cleanup oversized localStorage data
  // const cleanupOversizedData = useCallback(() => {
  //   try {
  //     const currentSize = getCurrentStorageSize();
  //     if (currentSize > MAX_STORAGE_SIZE) {
  //       // Clear history if it's too large
  //       setHistory([]);
  //       setCurrentIndex(-1);
  //       localStorage.removeItem(STORAGE_KEY);
  //     }
  //   } catch (error) {
  //     console.error('[HistoryContext] Error during cleanup:', error);
  //   }
  // }, [getCurrentStorageSize]); // Commented: unused function

  // Auto-cleanup when memory usage is high or when quota is exceeded
  useEffect(() => {
    const checkMemoryUsage = () => {
      try {
        const currentSize = getCurrentStorageSize();
        if (currentSize > MAX_STORAGE_SIZE * 0.7) { // 70% threshold (lowered from 80%)
          console.warn(`[HistoryContext] Memory usage high (${(currentSize / 1024 / 1024).toFixed(2)}MB), performing cleanup`);
          performCleanup();
        }
      } catch (error) {
        console.error('[HistoryContext] Error checking memory usage:', error);
      }
    };

    const interval = setInterval(checkMemoryUsage, 15000); // Check every 15 seconds (more frequent)
    return () => clearInterval(interval);
  }, [getCurrentStorageSize, performCleanup]);
  
  // Aggressive cleanup when localStorage quota is exceeded
  const handleQuotaExceeded = useCallback(() => {
    console.warn('[HistoryContext] localStorage quota exceeded, performing emergency cleanup');
    try {
      // Clear all localStorage items related to the app
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dnd_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Reset history completely
      setHistory([]);
      setCurrentIndex(-1);
      pendingCommandsRef.current = [];
      lastStateRef.current = {};
      
      console.log('[HistoryContext] Emergency cleanup completed');
    } catch (error) {
      console.error('[HistoryContext] Error during emergency cleanup:', error);
    }
  }, []);

  // Expose cleanup function for external use (e.g., when image upload fails)
  useEffect(() => {
    window.dndMapCleanupHistory = handleQuotaExceeded;
    return () => {
      delete window.dndMapCleanupHistory;
    };
  }, [handleQuotaExceeded]);
  
  // Manual cleanup function
  // const manualCleanup = useCallback(() => {
  //   try {
  //     performCleanup();
  //   } catch (error) {
  //     console.error('[HistoryContext] Error during manual cleanup:', error);
  //   }
  // }, [performCleanup]); // Commented: unused function
  
  // Force flush pending commands (useful for manual saves)
  const flushPending = useCallback(() => {
    clearTimeout(debounceTimeoutRef.current);
    flushPendingCommands();
  }, [flushPendingCommands]);
  
  // Get history info for debugging/UI
  const getHistoryInfo = useCallback(() => {
    return {
      totalCommands: history.length,
      pendingCommands: pendingCommandsRef.current.length,
      currentPosition: currentIndex + 1,
      canUndo: canUndo(),
      canRedo: canRedo(),
      memoryUsage: JSON.stringify(history).length,
      commands: history.map((cmd, idx) => ({
        ...cmd,
        isCurrent: idx === currentIndex
      }))
    };
  }, [history, currentIndex, canUndo, canRedo]);
  
  // Context value
  const value = {
    // Core functions
    pushCommand,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    flushPending,
    performCleanup,
    
    // Debug/UI helpers
    getHistoryInfo,
    isLoaded,
    
    // Constants and utilities
    COMMAND_TYPES,
    createDelta,
    applyDelta
  };
  
  return (
    <HistoryContext.Provider value={value}>
      {children}
    </HistoryContext.Provider>
  );
};

export default HistoryContext; 